import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { isPromoActive } from "@/utils/promo";
import { trackEvent } from "@/utils/analytics";

const CHECKOUT_URL = process.env.REACT_APP_BILLING_CHECKOUT_URL;

export const plans = [
  {
    name: "PRO",
    price: "99 €",
    period: "/mois",
    desc: "Pour démarrer et centraliser l'essentiel de votre activité.",
    benefits: ["Réservations & clients", "Planning", "Formules & options", "Caisse & dépenses", "1 établissement", "Jusqu'à 3 utilisateurs"],
    cta: "S'abonner",
    plan: "PRO",
    popular: false,
    testid: "pricing-card-pro",
  },
  {
    name: "BUSINESS",
    price: "149 €",
    period: "/mois",
    desc: "Pour piloter une exploitation complète depuis une seule plateforme.",
    benefits: ["Tout PRO", "Flotte", "Équipe", "Maintenance & incidents", "Partenaires & commissions", "Statistiques essentielles", "1 établissement", "Jusqu'à 10 utilisateurs", "Support prioritaire"],
    cta: "S'abonner",
    plan: "BUSINESS",
    popular: true,
    testid: "pricing-card-business",
  },
  {
    name: "PREMIUM",
    price: "249 €",
    period: "/mois",
    desc: "Pour les structures avancées et les exploitations multi-sites.",
    benefits: ["Tout BUSINESS", "Multi-sites", "Jusqu'à 10 établissements", "Utilisateurs illimités", "Analytics avancées", "Exports", "Pilotage consolidé multi-sites", "Support renforcé"],
    cta: "S'abonner",
    plan: "PREMIUM",
    popular: false,
    testid: "pricing-card-premium",
  },
  {
    name: "ENTERPRISE",
    price: "Sur devis",
    period: "",
    desc: "Pour les groupes, réseaux et déploiements à grande échelle.",
    benefits: ["Tout PREMIUM", "Besoins spécifiques", "Capacités sur mesure", "Déploiement personnalisé", "Accompagnement dédié"],
    cta: "Nous contacter",
    popular: false,
    testid: "pricing-card-enterprise",
  },
];

const PricingCards = () => {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const subscribe = async (planName) => {
    setLoadingPlan(planName);
    trackEvent("click_subscribe", { plan: planName, transport_type: "beacon" });
    try {
      const { data } = await axios.post(CHECKOUT_URL, { plan: planName });
      const url = data?.url || data?.checkout_url || data?.checkoutUrl || data?.session_url;
      if (typeof url !== "string" || !url.startsWith("https://")) throw new Error("invalid_checkout_url");
      trackEvent("checkout_started", { plan: planName, transport_type: "beacon" }, () => window.location.assign(url));
    } catch {
      toast.error("Impossible d'ouvrir le paiement pour le moment. Veuillez réessayer.");
      setLoadingPlan(null);
    }
  };

  return (
  <>
  <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
    {plans.map((p, i) => (
      <Reveal key={p.name} delay={0.07 * i} className="h-full">
        <div
          data-testid={p.testid}
          className={`relative flex h-full flex-col rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
            p.popular
              ? "border-brand bg-white shadow-glow"
              : "border-slate-200 bg-white shadow-soft hover:shadow-lift"
          }`}
        >
          {p.popular && (
            <span data-testid="pricing-popular-badge" className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand px-4 py-1 text-[11px] font-bold text-white">
              Le plus populaire
            </span>
          )}
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${p.popular ? "text-brand" : "text-navy/45"}`}>{p.name}</p>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="font-mono text-3xl font-bold text-navy">{p.price}</span>
            {p.period && <span className="text-sm font-medium text-navy/45">{p.period}</span>}
          </div>
          {p.plan && isPromoActive() && (
            <p data-testid={`${p.testid}-promo-setup`} className="mt-3 text-xs leading-relaxed text-navy/50">
              Mise en service & paramétrage : <span className="font-mono line-through">390 € HT</span>
              <span className="mt-0.5 block font-bold text-turquoise">0 € — OFFERT jusqu'au 30 septembre</span>
            </p>
          )}
          <p className="mt-4 text-sm leading-relaxed text-navy/60">{p.desc}</p>
          <ul className="mt-5 flex-1 space-y-2.5">
            {p.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm font-medium text-navy/75">
                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.popular ? "text-brand" : "text-turquoise"}`} /> {b}
              </li>
            ))}
          </ul>
          {p.plan ? (
            <button
              type="button"
              onClick={() => subscribe(p.plan)}
              disabled={loadingPlan !== null}
              data-testid={`${p.testid}-cta`}
              className={`group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-70 ${
                p.popular
                  ? "bg-brand text-white hover:bg-brand-700"
                  : "border border-slate-200 text-navy hover:border-brand/40 hover:text-brand"
              }`}
            >
              {loadingPlan === p.plan ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Redirection vers le paiement…
                </>
              ) : (
                <>
                  {p.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          ) : (
            <Link
              to="/contact"
              onClick={() => trackEvent("click_demo", { location: "pricing_enterprise" })}
              data-testid={`${p.testid}-cta`}
              className={`group mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-all duration-300 ${
                p.popular
                  ? "bg-brand text-white hover:bg-brand-700"
                  : "border border-slate-200 text-navy hover:border-brand/40 hover:text-brand"
              }`}
            >
              {p.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      </Reveal>
    ))}
  </div>
  <Reveal delay={0.2}>
    <p data-testid="setup-fee-note" className="mt-6 text-center text-xs leading-relaxed text-navy/45">
      {isPromoActive() ? (
        <>Frais de mise en service : <span className="font-mono font-semibold text-navy/60 line-through">390 € HT</span> <span className="font-bold text-turquoise">offerts jusqu'au 30 septembre 2026 inclus</span>, puis facturés une seule fois lors de l'inscription.</>
      ) : (
        <>Frais de mise en service : <span className="font-mono font-semibold text-navy/60">390 € HT</span>, facturés une seule fois lors de l'inscription.</>
      )}
    </p>
  </Reveal>
  </>
  );
};

export default PricingCards;
